import { clearTokens, decodeWorkspaceId, getAccessToken, getRefreshToken, setTokens } from './tokens';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const AUTH_API_BASE = import.meta.env.VITE_AUTH_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/auth';

async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body.detail === 'string') return body.detail;
    if (Array.isArray(body.detail)) {
      return body.detail.map((d: { msg?: string }) => d.msg).join('; ');
    }
    return 'Не удалось выполнить запрос';
  } catch {
    return 'Не удалось выполнить запрос';
  }
}

// A 15-minute access token expiring mid-session is normal, not an error — this
// silently redeems the (30-day, server-side revocable) refresh token for a new
// pair before the caller ever sees a 401. Shared across every client (auth/tasks/
// maps/layers) so it only has to be right once. De-duped so concurrent 401s from
// several in-flight requests trigger a single /auth/refresh call, not a stampede.
let refreshPromise: Promise<boolean> | null = null;

export async function tryRefreshSession(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const workspaceId = decodeWorkspaceId();
        const response = await fetch(`${AUTH_API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken, workspace_id: workspaceId }),
        });
        if (!response.ok) return false;
        const body = await response.json();
        setTokens(body.tokens.access_token, body.tokens.refresh_token);
        return true;
      } catch {
        return false;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

function redirectToLogin() {
  clearTokens();
  if (typeof window !== 'undefined') window.location.href = '/login';
}

export function createClient(baseUrl: string) {
  return async function request<T>(path: string, options: RequestInit = {}, auth = false, _retried = false): Promise<T> {
    // FormData bodies must NOT get a manual Content-Type — the browser sets
    // its own with the correct multipart boundary, and overriding it here
    // breaks server-side multipart parsing.
    const headers: Record<string, string> = {
      ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers as Record<string, string> | undefined),
    };

    if (auth) {
      const token = getAccessToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${baseUrl}${path}`, { ...options, headers });

    if (response.status === 401 && auth && !_retried) {
      if (await tryRefreshSession()) {
        return request<T>(path, options, auth, true);
      }
      redirectToLogin();
      throw new ApiError(401, 'сессия истекла');
    }

    if (!response.ok) {
      throw new ApiError(response.status, await extractErrorMessage(response));
    }

    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  };
}
